import DocumentViewerModal from "components/DocumentViewer";
import FileView from "components/fileView";
import { StatusChip } from "components/StatusChip";
import TeamView from "components/team_view";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createInternalLegalResearchRequest,
  createRespondBackFeedback,
  getLegalResearchRequest,
  updateLegalResearchRequest,
  updateLegalResearchRequestCommittee,
  updateLegalResearchTopic,
  updateResearchTeam,
} from "services/legalResearchService";
import { getResearchSchedules } from "services/researchScheduleService";
import { LoadingIcon } from "utils/constant";
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
} from "utils/constantValues";
import {
  calculateRemainingDays,
  formatDate,
  formatDay,
  getUserType,
} from "utils/helpers";
import AssignCoordinatorModal from "./assignCordinatorModal";
import ForwardToCoordinatorModal from "./forwardToCoordinatorModal";
import ManagementFeedback from "./managementFeedback";
import RespondBackForm from "./respondBackform";
import RespondBackToRequesterForm from "./respondBackToRequesterForm";
import SchedulePresentationModal from "./schedulePresentationModal";
import ScheduleTitlePresentationModal from "./scheduleTitlePresentationModal";
import SettingUpTeamModal from "./settingupTeamModal";
import TopicList from "./TopicList";
import UploadProposal from "./uploadProposal";
import EditTeamModal from "./editTeamModal";

type UrgencyLevel = "High" | "Medium" | "Low";
interface Schedule {
  id: string;
  location: string;
  purpose: string;
  remark: string;
  research_request: string;
  scheduled_datetime: string;
}

function ProposalDocument({ filteredData }: { filteredData: any }) {
  const { t } = useTranslation();
  type UrgencyLevel = "High" | "Medium" | "Low";
  const navigate = useNavigate();
  const { showToast } = useToast();
  const contextData = useAuth();
  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState(false);
  const [editTeamId, setEditTeamId] = useState<number>(0);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isForwardCoordinatorModalOpen, setIsForwardCoordinatorModalOpen] =
    useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleTitleModalOpen, setIsScheduleTitleModalOpen] = useState(false);
  const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
  const [isRespondBackToRequesterFormOpen, setIsRespondBackToRequesterFormOpen] = useState(false);
  const [isFeedbackFormOpen, setIsFeedBackFormOpen] = useState(false);
  const [showSynopsisHistory, setShowSynopsisHistory] = useState(false);
  const [showProposalHistory, setShowProposalHistory] = useState(false);
  const [viewTeam, setViewTeam] = useState(false);
  const [isProposalSummaryEditing, setIsProposalSummaryEditing] = useState(false);
  const [isUploadProposalModalOpen, setIsUploadProposalModalOpen] = useState(false);
  const [hasProposalBeenEdited, setHasProposalBeenEdited] = useState(false);

  useEffect(() => {
    const handleRefresh = (evt: Event) => {
      const event = evt as CustomEvent;
      setFormState((prev: any) => ({
        ...prev,
        ...event.detail,
      }));
    };

    window.addEventListener('refreshLegalResearch', handleRefresh);
    return () => {
      window.removeEventListener('refreshLegalResearch', handleRefresh);
    };
  }, []);

  const [schedules, setSchedules] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [teamId, setTeamId] = useState(0);
  const [remark, setRemark] = useState<string>("");
  const [respondBackStatus, setRespondBackStatus] = useState<string>(
    LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value,
  );
  const [respondBackForward, setRespondBackForward] = useState<string>(
    LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value,
  );

  const [formState, setFormState] = useState({
    ...filteredData,
    urgency_level: filteredData?.urgency_level
      ? filteredData?.urgency_level.charAt(0).toUpperCase() +
        filteredData?.urgency_level.slice(1).toLowerCase()
      : "High",
  });

  const [editedProposalSummary, setEditedProposalSummary] = useState(formState?.proposal_summary || "");
  const teamIndex = formState?.research_teams?.findIndex((team: any) =>
    team.prosecutors.some(
      (prosecutor: any) =>
        prosecutor.prosecutor_user_id === contextData.user.user_id,
    ),
  );

  // Whether the current user (director/coordinator) may edit / re-upload the proposal.
  const canEditProposal = (() => {
    const isDirector = contextData?.user?.roles?.some(
      (p) => p === "LRDIFUDDirector",
    );
    const isCoordinator = contextData?.user?.roles?.some(
      (p) => p === "LegalResearchCoordinator",
    );
    const editable =
      formState?.status === "proposal_requested" ||
      formState?.status ===
        LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value ||
      formState?.status === "proposal_approved";
    return (
      (isDirector &&
        formState?.forward_status !==
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
        editable) ||
      (isCoordinator &&
        formState?.forward_status !==
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
        editable)
    );
  })();

  useEffect(() => {
    getResearchSchedules(contextData.token)
      .then((data) => {
        const filtered = data.filter(
          (schedule: any) => schedule.research_request === formState.id,
        );

        const proposalSchedule = filtered?.find(
          (schedule: any) => schedule.purpose === "proposal_presentation",
        );

        setSchedules(proposalSchedule || filtered[0] || null);
      })
      .catch((error) => {
        console.error("Error fetching legal research request:", error);
        showToast(t("FailedToFetchLegalResearchRequest"), "error");
      });
  }, [setFormState]);

  const handleRemarkChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setRemark(event.target.value);
  };
  
  const handleAcceptTopics = async () => {
    try {
      const topics =
        formState.research_teams?.[teamIndex]?.topics?.map((topic: any) => ({
          ...topic,
          team_name: formState.research_teams?.[teamIndex]?.team_name,
        })) ||
        formState.internal_rsearc_topics ||
        [];

      let internal_response: any;

      for (const topic of topics) {
        if (
          formState.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value &&
          topic.is_approved
        ) {
          const formData = new FormData();
          formData.append("requested_by", formState.requested_by);
          formData.append(
            "forward_status",
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value,
          );
          formData.append(
            "status",
            LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value,
          );
          formData.append("research_task", formState.research_task);
          formData.append("research_topic", topic.topic_title);
          formData.append("research_task_id", formState.research_task_id);
          internal_response = await createInternalLegalResearchRequest(
            contextData.token,
            formData,
          );
        }
        const formData = new FormData();

        formData.append("topic_title", topic.topic_title);
        formData.append("is_accepted", String(topic.is_accepted));
        formData.append("is_approved", String(topic.is_approved));
        formData.append(
          "priority",
          topic.priority ? String(topic.priority) : "",
        );
        formData.append("research_request", topic.research_request);

        if (
          formState.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value &&
          internal_response
        )
          formData.append("internal_research_request", internal_response.id);

        await updateLegalResearchTopic(contextData.token, topic.id, formData);
      }

      const data = new FormData();
      if (
        formState.status ===
        LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value
      )
        data.append(
          "status",
          LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value,
        );
      else
        data.append(
          "status",
          LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value,
        );
      const response = await updateLegalResearchRequest(
        formState.id,
        data,
        contextData.token,
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("TopicsApproved"), "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateTitleApproval"), "error");
    }
  };

  const handleApproveTitle = async () => {
    setLoading(true);
    const data = new FormData();

    data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value);
    data.append("title_remark", remark);

    try {
      const response = await updateLegalResearchRequest(
        formState.id,
        data,
        contextData.token,
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("TitleApprovedSuccess"), "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateTitleApproval"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async (froward_status: string, status?: string) => {
    const data = new FormData();
    data.append("forward_status", froward_status);
    if (status) data.append("status", status);

    try {
      let response: any;
      if (formState.request_summary)
        response = await updateLegalResearchRequestCommittee(
          formState.id,
          data,
          contextData.token,
        );
      else
        response = await updateLegalResearchRequest(
          formState.id,
          data,
          contextData.token,
        );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("LegalResearchForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicForward = async (
    froward_status: string,
    status?: string,
  ) => {
    const data = new FormData();
    data.append("research_topic_status", froward_status);

    try {
      let response = await updateResearchTeam(
        contextData.token,
        formState.research_teams?.[teamIndex].id,
        data,
      );
      response = await getLegalResearchRequest(formState.id, contextData.token);

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      const forwarded = response.research_teams.every(
        (team: any) =>
          team.research_topic_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value,
      );

      if (forwarded) handleForward(froward_status);
      showToast(t("LegalResearchForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const data = new FormData();

    data.append("remark_for_director", remark);
    data.append("urgency_level", formState.urgency_level.toLowerCase());

    try {
      const response = await updateLegalResearchRequest(
        formState.id,
        data,
        contextData.token,
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("LegalResearchRespondedBackSuccess"), "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRespondBack = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    const data = new FormData();

    data.append("research_request", formState.id);
    data.append("given_by", contextData?.user?.full_name || "");
    data.append("role", getUserType(contextData?.user?.roles[0]));
    data.append("feedback_type", "stakeholder_request");
    data.append("note", remark);
    data.append("status", "respond_back");

    try {
      const feedbackResponse = await createRespondBackFeedback(
        contextData.token,
        data,
      );
      const researchData = new FormData();
      researchData.append(
        "status",
        LEGAL_RESEARCH_REQUEST_STATUS.responded.value,
      );
      const response = await updateLegalResearchRequest(
        formState.id,
        researchData,
        contextData.token,
      );
      setFormState((prev: any) => ({ ...prev, ...response }));
      setEdit(false);
      showToast(t("LegalResearchRespondedBackSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProposalRespondBack = async () => {
    setLoading(true);
    const data = new FormData();

    data.append(
      "status",
      LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value,
    );
    data.append(
      "forward_status",
      LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value,
    );
    data.append("feedback_from_management", remark);

    try {
      const response = await updateLegalResearchRequest(
        formState.id,
        data,
        contextData.token,
      );

      setFormState((prev: any) => ({ ...prev, response }));
      showToast(t("ProposalFeedbackSubmittedSuccess"), "success");
      setIsRespondBackFormOpen(false);
      setShowProposalHistory(false);
      window.dispatchEvent(new CustomEvent('refreshLegalResearch', { detail: response }));
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateProposalFeedback"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProposalReject = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    const data = new FormData();

    data.append(
      "status",
      LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value,
    );
    data.append("feedback_from_managment", remark);

    try {
      const response = await updateLegalResearchRequest(
        formState.id,
        data,
        contextData.token,
      );

      setFormState((prev: any) => ({ ...prev, response }));
      showToast(t("ProposalRejectedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateProposalFeedback"), "error");
    } finally {
      setLoading(false);
      setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }
  };

  const handleProposalRespondBackClick = () => {
    if (formState.request_summary) {
      setRespondBackStatus(
        LEGAL_RESEARCH_REQUEST_STATUS.research_committee_respondbacked.value,
      );
      setRespondBackForward(LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value);
    }
    setIsRespondBackFormOpen(!isRespondBackFormOpen);
  };

  const handleUpdateProposalSummary = async () => {
    setLoading(true);
    const data = new FormData();
    data.append("proposal_summary", editedProposalSummary);
    data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value);

    try {
      const response = await updateLegalResearchRequest(
        formState.id,
        data,
        contextData.token,
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        proposal_summary: editedProposalSummary,
      }));
      showToast(t("ProposalSummaryUpdatedSuccess"), "success");
      setIsProposalSummaryEditing(false);
      setHasProposalBeenEdited(true);
      setShowProposalHistory(false);
      window.dispatchEvent(new CustomEvent('refreshLegalResearch', { detail: response }));
    } catch (error) {
      console.error("Error updating proposal summary:", error);
      showToast(t("FailedToUpdateProposalSummary"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProposalApprove = async () => {
    setLoading(true);
    const data = new FormData();
    
    const isCoordinator = contextData?.user?.roles?.some(
      (p) => p === "LegalResearchCoordinator",
    );
    const isDirector = contextData?.user?.roles?.some(
      (p) => p === "LRDIFUDDirector",
    );
    
    if (formState.request_summary) {
      data.append(
        "status",
        LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value,
      );
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value,
      );
    } else if (isDirector) {
      data.append(
        "status",
        LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value,
      );
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value,
      );
    } else if (isCoordinator) {
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value,
      );
    } else {
      data.append(
        "status",
        LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value,
      );
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value,
      );
    }

    try {
      let response: any;
      if (!formState.request_summary)
        response = await updateLegalResearchRequest(
          formState.id,
          data,
          contextData.token,
        );
      else
        response = await updateLegalResearchRequestCommittee(
          formState.id,
          data,
          contextData.token,
        );

      const updatedState = {
        ...response,
        status: formState.request_summary
          ? LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value
          : isDirector
            ? LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value
            : formState.status,
        forward_status: formState.request_summary
          ? LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value
          : isDirector
            ? LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
            : isCoordinator
              ? LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value
              : LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value,
      };
      setFormState((prev: any) => ({ ...prev, ...updatedState }));
      showToast(t("ProposalApprovedSuccess"), "success");
      setShowProposalHistory(false);
      setHasProposalBeenEdited(false);
      window.dispatchEvent(new CustomEvent('refreshLegalResearch', { detail: updatedState }));
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateProposalApproval"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUrgencyClick = (level: UrgencyLevel) => {
    if (formState.status != LEGAL_RESEARCH_REQUEST_STATUS.pending.value) return;
    setFormState((prev: any) => ({
      ...prev,
      urgency_level: level,
    }));
  };

  const getUrgencyButtonClasses = (level: UrgencyLevel) => {
    const baseClasses =
      "px-4 py-2 rounded-md font-semibold transition-colors duration-200";
    if (formState.urgency_level === level) {
      switch (level) {
        case "High":
          return `${baseClasses} bg-red-500 text-white`;
        case "Medium":
          return `${baseClasses} bg-yellow-500 text-white`;
        case "Low":
          return `${baseClasses} bg-green-500 text-white`;
        default:
          return "";
      }
    } else {
      return `${baseClasses} bg-gray-200 text-gray-700 hover:bg-gray-300`;
    }
  };

  // Director (acting as head) forwards a committee request to a coordinator,
  // optionally with a remark for the coordinator.
  const handleCommitteeForwardToCoordinator = async () => {
    setLoading(true);
    const data = new FormData();
    data.append(
      "forward_status",
      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value,
    );
    data.append(
      "status",
      LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value,
    );
    if (remark) data.append("remark_for_coordinator", remark);

    try {
      const response = await updateLegalResearchRequestCommittee(
        formState.id,
        data,
        contextData.token,
      );
      setFormState((prev: any) => ({
        ...prev,
        ...response,
        remark_for_coordinator:
          remark || response?.remark_for_coordinator || prev.remark_for_coordinator,
      }));
      showToast(t("LegalResearchForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProposalClick = () => {
    setIsModalOpen(true);
  };

  const handleReuploadProposal = (data: { proposalSummary: string; proposalFile: string }) => {
    getLegalResearchRequest(formState.id, contextData.token)
      .then((response) => {
        setFormState((prev: any) => ({
          ...prev,
          ...response,
          proposal_summary: data.proposalSummary,
        }));
        setHasProposalBeenEdited(true);
      })
      .catch((error) => {
        console.error("Error refreshing proposal:", error);
      });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSchduleCloseModal = () => {
    setIsScheduleModalOpen(false);
  };

  const handleSchduleTitleCloseModal = () => {
    setIsScheduleTitleModalOpen(false);
  };

  const handleCloseTeamModal = () => {
    setIsTeamModalOpen(false);
  };

  const handleAssignProsecutorClick = () => {
    setIsTeamModalOpen(true);
  };

  const handleAssignCoordinatorClick = () => {
    setIsAssignModalOpen(true);
  };

  const handleAssignCloseModal = () => {
    setIsAssignModalOpen(false);
  };

  return (
    <>
      <div className="flex h-full grow flex-col max-w-4xl bg-white rounded-lg shadow-lg p-6 overflow-y-scroll scrollbar-hide">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-red-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
          <h1 className="text-1.5xl font-bold text-red-900 uppercase tracking-wide">
            {t("ConductLegalResearchRequest")}
          </h1>
          <div className="flex gap-4">
            <div className="my-auto">
              <StatusChip
                status={
                  Object.values(LEGAL_RESEARCH_REQUEST_STATUS)?.find(
                    (s) => s.value === formState?.status,
                  )?.label ||
                  formState?.status ||
                  ""
                }
              />
            </div>
            <div className="my-auto">
              <span className="text-sm text-gray-600">{t("ForwardStatus")}: </span>
              <StatusChip
                status={
                  Object.values(LEGAL_RESEARCH_REQUEST_STATUS)?.find(
                    (s) => s.value === formState?.forward_status,
                  )?.label ||
                  formState?.forward_status ||
                  ""
                }
              />
            </div>
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
              formState.requested_by === "internal" &&
              formState.research_teams?.every(
                (team: any) =>
                  team.research_topic_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value,
              ) &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchProsecutor",
              ) && (
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                        .value,
                    )
                  }
                  type="button"
                  className="bg-red-900 hover:bg-red-800 text-white rounded-lg px-4 font-semibold"
                >
                  {t("ForwardToCoordinator")}
                </button>
              )}
            {formState.request_summary &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.research_committee_underway
                  .value && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value,
                        LEGAL_RESEARCH_REQUEST_STATUS.research_committee_closed
                          .value,
                      )
                    }
                    type="button"
                    className="bg-red-900 hover:bg-red-800 text-white rounded-lg px-4 font-semibold"
                  >
                    {t("CloseReport")}
                  </button>
                </div>
              )}
            {formState.remark_for_director &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (
                <div className="flex gap-4">
                  <button
                    onClick={() => setEdit(!edit)}
                    type="button"
                    className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                  >
                    {edit ? t("Cancel") : (
                      <svg
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                          fill="red"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd
                  .value &&
              contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-red-900 hover:bg-red-800 text-white rounded-lg px-4 font-semibold"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
              (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") || 
               contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director
                          .value,
                        location.pathname.includes("/committee")
                          ? LEGAL_RESEARCH_REQUEST_STATUS
                              .research_committee_acceptd.value
                          : LEGAL_RESEARCH_REQUEST_STATUS.pending.value,
                      )
                    }
                    type="button"
                    className="bg-red-900 hover:bg-red-800 text-white rounded-lg px-4 font-semibold"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {(formState.remark_for_team || formState.request_summary) &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              (formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value ||
                formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value) &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.proposal_summary &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
              (formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.proposal_requested.value ||
                formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value) &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchProsecutor",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.schedules?.find(
              (schedule: any) => schedule.purpose === "proposal_presentation",
            ) &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value &&
              (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
               contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS
                .proposal_presentation_feedback_submitted.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value,
                        LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.received.value &&
              (formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value ||
                formState.forward_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                    .value) &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator",
              ) && (
                <div className="flex gap-4">
                  {formState.requested_by === "internal" && (
                    <div className="flex gap-4">
                      <button
                        onClick={() =>
                          navigate(`/conduct-legal-research/internalRequest/`, {
                            state: { formState: formState },
                          })
                        }
                        type="button"
                        className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                      >
                        {!edit ? (
                          <svg
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                              fill="black"
                            />
                          </svg>
                        ) : t("Cancel")}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("ForwardToProsecutor")}
                  </button>
                </div>
              )}
            {/* Director who created the internal research forwards it to a coordinator or straight to the prosecutors */}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.received.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      navigate(`/conduct-legal-research/internalRequest/`, {
                        state: { formState: formState },
                      })
                    }
                    type="button"
                    className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                  >
                    {!edit ? (
                      <svg
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                          fill="black"
                        />
                      </svg>
                    ) : t("Cancel")}
                  </button>
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-white border border-gray-800 text-gray-800 hover:bg-gray-100 rounded-lg px-4 py-2"
                  >
                    {t("ForwardToCoordinator")}
                  </button>
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4 py-2"
                  >
                    {t("ForwardToProsecutor")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value &&
              formState.research_teams?.[teamIndex]?.research_topic_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchProsecutor",
              ) && (
                <div className="flex gap-4">
                  {formState.requested_by === "internal" && (
                    <div className="flex gap-4">
                      <button
                        onClick={() =>
                          navigate(
                            `/conduct-legal-research/editInternalrequest/${formState.id}`,
                            { state: { formState: formState } },
                          )
                        }
                        type="button"
                        className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                      >
                        {!edit ? (
                          <svg
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                              fill="black"
                            />
                          </svg>
                        ) : t("Cancel")}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() =>
                      handleTopicForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
              formState.requested_by === "internal" &&
              (contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator",
              ) ||
                contextData?.user?.roles?.some(
                  (p) => p === "LRDIFUDDirector",
                )) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("CloseTopicSubmission")}
                  </button>
                </div>
              )}
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              !formState.schedules?.find(
                (schedule: any) => schedule.purpose === "title_presentation",
              ) &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator",
              ) && (
                <div className="flex gap-4">
                  <div className="flex gap-4">
                    <button
                      onClick={() => setEdit(!edit)}
                      type="button"
                      className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                    >
                      {!edit ? (
                        <svg
                          width="25"
                          height="24"
                          viewBox="0 0 25 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                            fill="black"
                          />
                        </svg>
                      ) : t("Cancel")}
                    </button>
                  </div>
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
              !formState.schedules?.find(
                (schedule: any) => schedule.purpose === "title_presentation",
              ) &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value,
                      )
                    }
                    type="button"
                    className="bg-red-900 hover:bg-red-800 text-white rounded-lg px-4 font-semibold"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.schedules?.find(
              (schedule: any) => schedule.purpose === "title_presentation",
            ) &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.requested_by === "internal" &&
              (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
               contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "OAGBManagementHead",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator",
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() => setIsFeedBackFormOpen(!isFeedbackFormOpen)}
                    type="button"
                    className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                  >
                    {isFeedbackFormOpen ? t("Cancel") : t("AddFeedBack")}
                  </button>
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value,
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
          </div>
        </div>

        {(formState.documents.find(
          (document: any) =>
            typeof document.document_type === "string" &&
            document.document_type.includes("completion"),
        ) || formState?.research_completion_letter_file) && (
          <>
            <div className="flex mb-4">
              <p className="font-semibold text-base mb-1">
                {t("RegistrarLetterReferenceNumber")}:
              </p>
              <p className="font-semibold text-base mb-1">
                {formState.letter_reference_number}
              </p>
            </div>
            <div className="max-w-4xl bg-white rounded-lg p-6 border border-gray-400 mb-4 h-fit">
              <p className="font-semibold text-base mb-1">{t("MessengerDetail")}</p>
              <p className="text-[#B8B8B8] font-regular text-sm mb-1">
                {formState?.messenger_name}
              </p>
              {formState.documents.find(
                (document: any) =>
                  typeof document.document_type === "string" &&
                  document.document_type.includes("completion"),
              ) ? (
                <DocumentViewerModal
                  documents={formState?.documents.filter(
                    (document: any) =>
                      typeof document.document_type === "string" &&
                      document.document_type.includes("completion"),
                  )}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                />
              ) : (
                <FileView
                  title={t("ResearchCompletionLetterFile")}
                  file={formState?.research_completion_letter_file}
                  fileName={formState?.research_completion_letter_file &&
                    formState.research_completion_letter_file.substring(
                      formState.research_completion_letter_file.lastIndexOf("/") + 1,
                    )}
                />
              )}
            </div>
          </>
        )}
        {!formState.proposal_summary &&
          (formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value) &&
          formState.proposal_deadline && (
            <div className="bg-[#F7F7F7] py-3 px-4 mb-2 border border-gray-400 rounded-lg">
              <div className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="my-auto"
                  width="37"
                  height="37"
                  viewBox="0 0 37 37"
                  fill="none"
                >
                  <path
                    d="M18.5007 22.083C18.855 22.083 19.2014 21.9779 19.496 21.7811C19.7907 21.5842 20.0203 21.3044 20.1559 20.977C20.2915 20.6496 20.327 20.2894 20.2579 19.9418C20.1888 19.5943 20.0181 19.275 19.7675 19.0244C19.517 18.7739 19.1977 18.6032 18.8502 18.5341C18.5026 18.465 18.1424 18.5005 17.815 18.6361C17.4876 18.7717 17.2078 19.0013 17.0109 19.2959C16.8141 19.5906 16.709 19.937 16.709 20.2913C16.709 20.7665 16.8977 21.2222 17.2338 21.5582C17.5698 21.8942 18.0255 22.083 18.5007 22.083ZM27.459 22.083C27.8133 22.083 28.1597 21.9779 28.4544 21.7811C28.749 21.5842 28.9787 21.3044 29.1143 20.977C29.2499 20.6496 29.2854 20.2894 29.2162 19.9418C29.1471 19.5943 28.9765 19.275 28.7259 19.0244C28.4753 18.7739 28.1561 18.6032 27.8085 18.5341C27.461 18.465 27.1007 18.5005 26.7733 18.6361C26.446 18.7717 26.1661 19.0013 25.9693 19.2959C25.7724 19.5906 25.6673 19.937 25.6673 20.2913C25.6673 20.7665 25.8561 21.2222 26.1921 21.5582C26.5281 21.8942 26.9838 22.083 27.459 22.083ZM18.5007 29.2497C18.855 29.2497 19.2014 29.1446 19.496 28.9477C19.7907 28.7509 20.0203 28.471 20.1559 28.1436C20.2915 27.8163 20.327 27.456 20.2579 27.1085C20.1888 26.7609 20.0181 26.4417 19.7675 26.1911C19.517 25.9405 19.1977 25.7699 18.8502 25.7008C18.5026 25.6316 18.1424 25.6671 17.815 25.8027C17.4876 25.9383 17.2078 26.168 17.0109 26.4626C16.8141 26.7572 16.709 27.1036 16.709 27.458C16.709 27.9332 16.8977 28.3889 17.2338 28.7249C17.5698 29.0609 18.0255 29.2497 18.5007 29.2497ZM27.459 29.2497C27.8133 29.2497 28.1597 29.1446 28.4544 28.9477C28.749 28.7509 28.9787 28.471 29.1143 28.1436C29.2499 27.8163 29.2854 27.456 29.2162 27.1085C29.1471 26.7609 28.9765 26.4417 28.7259 26.1911C28.4753 25.9405 28.1561 25.7699 27.8085 25.7008C27.461 25.6316 27.1007 25.6671 26.7733 25.8027C26.446 25.9383 26.1661 26.168 25.9693 26.4626C25.7724 26.7572 25.6673 27.1036 25.6673 27.458C25.6673 27.9332 25.8561 28.3889 26.1921 28.7249C26.5281 29.0609 26.9838 29.2497 27.459 29.2497ZM9.54232 22.083C9.89668 22.083 10.2431 21.9779 10.5377 21.7811C10.8324 21.5842 11.062 21.3044 11.1976 20.977C11.3332 20.6496 11.3687 20.2894 11.2996 19.9418C11.2304 19.5943 11.0598 19.275 10.8092 19.0244C10.5586 18.7739 10.2394 18.6032 9.89185 18.5341C9.5443 18.465 9.18406 18.5005 8.85668 18.6361C8.52929 18.7717 8.24947 19.0013 8.0526 19.2959C7.85573 19.5906 7.75065 19.937 7.75065 20.2913C7.75065 20.7665 7.93942 21.2222 8.27542 21.5582C8.61142 21.8942 9.06714 22.083 9.54232 22.083ZM31.0423 4.16634H29.2507V2.37467C29.2507 1.89949 29.0619 1.44378 28.7259 1.10777C28.3899 0.771772 27.9342 0.583008 27.459 0.583008C26.9838 0.583008 26.5281 0.771772 26.1921 1.10777C25.8561 1.44378 25.6673 1.89949 25.6673 2.37467V4.16634H11.334V2.37467C11.334 1.89949 11.1452 1.44378 10.8092 1.10777C10.4732 0.771772 10.0175 0.583008 9.54232 0.583008C9.06714 0.583008 8.61142 0.771772 8.27542 1.10777C7.93942 1.44378 7.75065 1.89949 7.75065 2.37467V4.16634H5.95898C4.53345 4.16634 3.16629 4.73263 2.15829 5.74064C1.15028 6.74865 0.583984 8.1158 0.583984 9.54134V31.0413C0.583984 32.4669 1.15028 33.834 2.15829 34.842C3.16629 35.85 4.53345 36.4163 5.95898 36.4163H31.0423C32.4679 36.4163 33.835 35.85 34.843 34.842C35.851 33.834 36.4173 32.4669 36.4173 31.0413V9.54134C36.4173 8.1158 35.851 6.74865 34.843 5.74064C33.835 4.73263 32.4679 4.16634 31.0423 4.16634ZM32.834 31.0413C32.834 31.5165 32.6452 31.9722 32.3092 32.3082C31.9732 32.6442 31.5175 32.833 31.0423 32.833H5.95898C5.4838 32.833 5.02809 32.6442 4.69208 32.3082C4.35608 31.9722 4.16732 31.5165 4.16732 31.0413V14.9163H32.834V31.0413ZM32.834 11.333H4.16732V9.54134C4.16732 9.06616 4.35608 8.61044 4.69208 8.27444C5.02809 7.93844 5.4838 7.74967 5.95898 7.74967H31.0423C31.5175 7.74967 31.9732 7.93844 32.3092 8.27444C32.6452 8.61044 32.834 9.06616 32.834 9.54134V11.333ZM9.54232 29.2497C9.89668 29.2497 10.2431 29.1446 10.5377 28.9477C10.8324 28.7509 11.062 28.471 11.1976 28.1436C11.3332 27.8163 11.3687 27.456 11.2996 27.1085C11.2304 26.7609 11.0598 26.4417 10.8092 26.1911C10.5586 25.9405 10.2394 25.7699 9.89185 25.7008C9.5443 25.6316 9.18406 25.6671 8.85668 25.8027C8.52929 25.9383 8.24947 26.168 8.0526 26.4626C7.85573 26.7572 7.75065 27.1036 7.75065 27.458C7.75065 27.9332 7.93942 28.3889 8.27542 28.7249C8.61142 29.0609 9.06714 29.2497 9.54232 29.2497Z"
                    fill="black"
                  />
                </svg>
                <div className="ml-4 my-auto">
                  <p className="text-sm text-[#212121] font-semibold">
                    {t("DeadlineForProposal")}
                  </p>
                  {(() => {
                    const days = calculateRemainingDays(
                      formState.proposal_deadline,
                    );
                    const label =
                      days < 0
                        ? t("OverdueByDays", { count: Math.abs(days) })
                        : days === 0
                          ? t("Today")
                          : t("DaysLeft", { count: days });
                    const cls =
                      days < 0
                        ? "bg-red-100 text-red-700"
                        : days === 0
                          ? "bg-orange-100 text-orange-700"
                          : days <= 3
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700";
                    return (
                      <span
                        className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-base font-normal ml-auto flex my-auto">
                  <p className="font-semibold mr-2">
                    {formatDay(formState.proposal_deadline).weekday},
                  </p>
                  <p>{formatDay(formState.proposal_deadline).rest}</p>
                </div>
              </div>
            </div>
          )}
        {schedules &&
          formState.schedules?.find(
            (schedule: any) => schedule.purpose === "proposal_presentation",
          ) && (
            <div className="p-4 border border-gray-200 rounded-xl shadow-sm bg-white">
              <div
                onClick={() => {
                  !(
                    formState?.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
                  )
                    ? setIsFeedBackFormOpen(!isFeedbackFormOpen)
                    : setIsScheduleModalOpen(!isScheduleModalOpen);
                }}
                className="flex items-center gap-3 cursor-pointer"
              >
                <div className="w-11 h-11 rounded-lg bg-[#0A1D39] flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-[#9B0505] font-semibold">
                    {t("OfficeOfTheAttorneyGeneral") ||
                      "Office of the Attorney General"}
                  </p>
                  <p className="text-sm font-bold text-[#0A1D39]">
                    {t("ProposalPresentation")}
                  </p>
                  {schedules.location && (
                    <p className="text-xs text-gray-500 truncate">
                      📍 {schedules?.location}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-[#0A1D39]">
                    {formatDay(schedules.scheduled_datetime).weekday}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDay(schedules.scheduled_datetime).rest}
                  </p>
                  {schedules.scheduled_datetime &&
                    (() => {
                      const days = calculateRemainingDays(
                        schedules.scheduled_datetime,
                      );
                      const label =
                        days < 0
                          ? t("OverdueByDays", { count: Math.abs(days) })
                          : days === 0
                            ? t("Today")
                            : t("DaysLeft", { count: days });
                      const cls =
                        days < 0
                          ? "bg-red-100 text-red-700"
                          : days === 0
                            ? "bg-orange-100 text-orange-700"
                            : days <= 3
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700";
                      return (
                        <span
                          className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}
                        >
                          {label}
                        </span>
                      );
                    })()}
                </div>
              </div>
              {(contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector",
              ) ||
                contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator",
                )) &&
                schedules?.purpose === "proposal_presentation" &&
                formState.forward_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                    .value &&
                formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value && (
                  <ManagementFeedback
                    setIsFeedBackFormOpen={setIsFeedBackFormOpen}
                    status={
                      LEGAL_RESEARCH_REQUEST_STATUS
                        .proposal_presentation_feedback_submitted.value
                    }
                    formState={formState}
                    setFormState={setFormState}
                    setIsRespondBackFormOpen={setIsRespondBackFormOpen}
                  />
                )}
              {formState.feedback_from_management &&
                formState.status !==
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h2 className="text-base text-[#212121] font-semibold">
                      {formState.status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value
                        ? t("ProposalRejectionReason")
                        : formState.status ===
                            LEGAL_RESEARCH_REQUEST_STATUS
                              .proposal_presentation_feedback_submitted.value
                          ? t("ProposalPresentationFeedback") ||
                            "Proposal Presentation Feedback"
                          : t("ProposalFileFeedback")}
                    </h2>
                    <p className="mt-2 text-[#606060]">
                      {formState.feedback_from_management}
                    </p>
                    {formState.documents.find(
                      (document: any) =>
                        document.document_type === "management_feedback" ||
                        document.document_type ===
                          "feedback_from_management_file",
                    ) && (
                      <DocumentViewerModal
                        documents={formState?.documents.filter(
                          (document: any) =>
                            document.document_type === "management_feedback" ||
                            document.document_type ===
                              "feedback_from_management_file",
                        )}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                      />
                    )}
                  </div>
                )}
            </div>
          )}
        {schedules && schedules?.purpose === "title_presentation" && (
          <div className="flex py-3 px-4 border border-gray-400 rounded-lg mb-4">
            <div className="flex-1">
              <div
                onClick={() => {
                  if (formState.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
                      formState.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value) {
                    setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
                  } else {
                    setIsScheduleTitleModalOpen(false);
                  }
                }}
                className="flex"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="my-auto"
                  width="37"
                  height="37"
                  viewBox="0 0 37 37"
                  fill="none"
                >
                  <path
                    d="M18.5007 22.083C18.855 22.083 19.2014 21.9779 19.496 21.7811C19.7907 21.5842 20.0203 21.3044 20.1559 20.977C20.2915 20.6496 20.327 20.2894 20.2579 19.9418C20.1888 19.5943 20.0181 19.275 19.7675 19.0244C19.517 18.7739 19.1977 18.6032 18.8502 18.5341C18.5026 18.465 18.1424 18.5005 17.815 18.6361C17.4876 18.7717 17.2078 19.0013 17.0109 19.2959C16.8141 19.5906 16.709 19.937 16.709 20.2913C16.709 20.7665 16.8977 21.2222 17.2338 21.5582C17.5698 21.8942 18.0255 22.083 18.5007 22.083ZM27.459 22.083C27.8133 22.083 28.1597 21.9779 28.4544 21.7811C28.749 21.5842 28.9787 21.3044 29.1143 20.977C29.2499 20.6496 29.2854 20.2894 29.2162 19.9418C29.1471 19.5943 28.9765 19.275 28.7259 19.0244C28.4753 18.7739 28.1561 18.6032 27.8085 18.5341C27.461 18.465 27.1007 18.5005 26.7733 18.6361C26.446 18.7717 26.1661 19.0013 25.9693 19.2959C25.7724 19.5906 25.6673 19.937 25.6673 20.2913C25.6673 20.7665 25.8561 21.2222 26.1921 21.5582C26.5281 21.8942 26.9838 22.083 27.459 22.083ZM18.5007 29.2497C18.855 29.2497 19.2014 29.1446 19.496 28.9477C19.7907 28.7509 20.0203 28.471 20.1559 28.1436C20.2915 27.8163 20.327 27.456 20.2579 27.1085C20.1888 26.7609 20.0181 26.4417 19.7675 26.1911C19.517 25.9405 19.1977 25.7699 18.8502 25.7008C18.5026 25.6316 18.1424 25.6671 17.815 25.8027C17.4876 25.9383 17.2078 26.168 17.0109 26.4626C16.8141 26.7572 16.709 27.1036 16.709 27.458C16.709 27.9332 16.8977 28.3889 17.2338 28.7249C17.5698 29.0609 18.0255 29.2497 18.5007 29.2497ZM27.459 29.2497C27.8133 29.2497 28.1597 29.1446 28.4544 28.9477C28.749 28.7509 28.9787 28.471 29.1143 28.1436C29.2499 27.8163 29.2854 27.456 29.2162 27.1085C29.1471 26.7609 28.9765 26.4417 28.7259 26.1911C28.4753 25.9405 28.1561 25.7699 27.8085 25.7008C27.461 25.6316 27.1007 25.6671 26.7733 25.8027C26.446 25.9383 26.1661 26.168 25.9693 26.4626C25.7724 26.7572 25.6673 27.1036 25.6673 27.458C25.6673 27.9332 25.8561 28.3889 26.1921 28.7249C26.5281 29.0609 26.9838 29.2497 27.459 29.2497ZM9.54232 22.083C9.89668 22.083 10.2431 21.9779 10.5377 21.7811C10.8324 21.5842 11.062 21.3044 11.1976 20.977C11.3332 20.6496 11.3687 20.2894 11.2996 19.9418C11.2304 19.5943 11.0598 19.275 10.8092 19.0244C10.5586 18.7739 10.2394 18.6032 9.89185 18.5341C9.5443 18.465 9.18406 18.5005 8.85668 18.6361C8.52929 18.7717 8.24947 19.0013 8.0526 19.2959C7.85573 19.5906 7.75065 19.937 7.75065 20.2913C7.75065 20.7665 7.93942 21.2222 8.27542 21.5582C8.61142 21.8942 9.06714 22.083 9.54232 22.083ZM31.0423 4.16634H29.2507V2.37467C29.2507 1.89949 29.0619 1.44378 28.7259 1.10777C28.3899 0.771772 27.9342 0.583008 27.459 0.583008C26.9838 0.583008 26.5281 0.771772 26.1921 1.10777C25.8561 1.44378 25.6673 1.89949 25.6673 2.37467V4.16634H11.334V2.37467C11.334 1.89949 11.1452 1.44378 10.8092 1.10777C10.4732 0.771772 10.0175 0.583008 9.54232 0.583008C9.06714 0.583008 8.61142 0.771772 8.27542 1.10777C7.93942 1.44378 7.75065 1.89949 7.75065 2.37467V4.16634H5.95898C4.53345 4.16634 3.16629 4.73263 2.15829 5.74064C1.15028 6.74865 0.583984 8.1158 0.583984 9.54134V31.0413C0.583984 32.4669 1.15028 33.834 2.15829 34.842C3.16629 35.85 4.53345 36.4163 5.95898 36.4163H31.0423C32.4679 36.4163 33.835 35.85 34.843 34.842C35.851 33.834 36.4173 32.4669 36.4173 31.0413V9.54134C36.4173 8.1158 35.851 6.74865 34.843 5.74064C33.835 4.73263 32.4679 4.16634 31.0423 4.16634ZM32.834 31.0413C32.834 31.5165 32.6452 31.9722 32.3092 32.3082C31.9732 32.6442 31.5175 32.833 31.0423 32.833H5.95898C5.4838 32.833 5.02809 32.6442 4.69208 32.3082C4.35608 31.9722 4.16732 31.5165 4.16732 31.0413V14.9163H32.834V31.0413ZM32.834 11.333H4.16732V9.54134C4.16732 9.06616 4.35608 8.61044 4.69208 8.27444C5.02809 7.93844 5.4838 7.74967 5.95898 7.74967H31.0423C31.5175 7.74967 31.9732 7.93844 32.3092 8.27444C32.6452 8.61044 32.834 9.06616 32.834 9.54134V11.333ZM9.54232 29.2497C9.89668 29.2497 10.2431 29.1446 10.5377 28.9477C10.8324 28.7509 11.062 28.471 11.1976 28.1436C11.3332 27.8163 11.3687 27.456 11.2996 27.1085C11.2304 26.7609 11.0598 26.4417 10.8092 26.1911C10.5586 25.9405 10.2394 25.7699 9.89185 25.7008C9.5443 25.6316 9.18406 25.6671 8.85668 25.8027C8.52929 25.9383 8.24947 26.168 8.0526 26.4626C7.85573 26.7572 7.75065 27.1036 7.75065 27.458C7.75065 27.9332 7.93942 28.3889 8.27542 28.7249C8.61142 29.0609 9.06714 29.2497 9.54232 29.2497Z"
                    fill="black"
                  />
                </svg>
                <div className="flex-1 ml-4 my-auto">
                  <p className="text-sm text-[#212121] font-semibold">
                    {formState.requested_by === "internal" ? t("Topic") : t("Title")} {t("Presentation")}
                  </p>
                  {schedules.location && (
                    <p className="text-sm text-[#A4A4A4] font-normal">
                      {schedules?.location}
                    </p>
                  )}
                </div>
                <div className="text-base font-normal ml-auto flex my-auto">
                  <p className="font-semibold mr-2">
                    {formatDay(schedules.scheduled_datetime).weekday},
                  </p>
                  <p>{formatDay(schedules.scheduled_datetime).rest}</p>
                  {formState.status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
                    formState.title_presentation_feedback && (
                      <div
                        className="ml-4 hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2 cursor-pointer"
                        onClick={() =>
                          setIsFeedBackFormOpen(!isFeedbackFormOpen)
                        }
                      >
                        {isFeedbackFormOpen ? t("Cancel") : t("AddFeedBack")}
                      </div>
                    )}
                </div>
              </div>
              {(formState.requested_by !== "internal" &&
                !formState.title_presentation_feedback &&
                !(
                  formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value
                ) &&
                !(
                  formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value
                ) &&
                formState.forward_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                    .value &&
                (contextData?.user?.roles?.some(
                  (p) => p === "LRDIFUDDirector",
                ) ||
                  contextData?.user?.roles?.some(
                    (p) => p === "LegalResearchCoordinator",
                  )) &&
                schedules?.purpose === "title_presentation") ||
              (isFeedbackFormOpen &&
                !(
                  formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value
                )) ? (
                <>
                  <br />
                  <ManagementFeedback
                    setIsFeedBackFormOpen={setIsFeedBackFormOpen}
                    status={
                      LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value
                    }
                    formState={formState}
                    setFormState={setFormState}
                    setIsRespondBackFormOpen={setIsRespondBackFormOpen}
                  />
                </>
              ) : (
                formState.title_presentation_feedback && (
                  <div className="mt-4">
                    <p className="font-medium mb-2">
                      {t("TitleRemarkFromManagement")}
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      {formState.title_presentation_feedback}
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        <div className="flex-1 mt-4">
          {/* Request Details Grid */}
          <div>
            {formState.requested_by === "internal" && (
              <>
                <div className="flex gap-2 mb-4">
                  <p className="font-semibold text-lg">{t("ResearchTask")}: </p>
                  <p className="mt-auto">{formState.research_task}</p>
                </div>
                {(formState?.internal_rsearc_topics.length > 0 ||
                  formState?.research_teams?.[teamIndex]?.topics?.length >
                    0) && (
                  <>
                    <TopicList
                      formState={
                        contextData?.user?.roles?.some(
                          (p) =>
                            p === "LegalResearchCoordinator" ||
                            p === "LRDIFUDDirector" ||
                            p === "OAGBManagementHead",
                        )
                          ? formState.research_teams?.flatMap(
                              (team: any) =>
                                team.topics?.map((topic: any) => ({
                                  ...topic,
                                  team_name: team.team_name,
                                })) || [],
                            ) || formState.internal_rsearc_topics
                          : formState.research_teams?.[teamIndex]?.topics?.map(
                              (topic: any) => ({
                                ...topic,
                                team_name:
                                  formState.research_teams?.[teamIndex]
                                    ?.team_name,
                              }),
                            ) || formState.internal_rsearc_topics
                      }
                      status={formState.status}
                      updateTopics={(updater) => {
                        if (
                          contextData?.user?.roles?.some(
                            (p) =>
                              p === "LegalResearchCoordinator" ||
                              p === "LRDIFUDDirector" ||
                              p === "OAGBManagementHead",
                          )
                        ) {
                          setFormState((prev: any) => {
                            const updatedTopics = updater(
                              prev.internal_rsearc_topics,
                            );
                            const updatedTeams = prev.research_teams.map(
                              (team: any) => ({
                                ...team,
                                topics: team.topics.map((topic: any) => {
                                  const updated = updatedTopics.find(
                                    (t: any) => t.id === topic.id,
                                  );
                                  return updated || topic;
                                }),
                              }),
                            );
                            return {
                              ...prev,
                              internal_rsearc_topics: updatedTopics,
                              research_teams: updatedTeams,
                            };
                          });
                        } else if (
                          formState.research_teams?.[teamIndex]?.topics
                        ) {
                          setFormState((prev: any) => ({
                            ...prev,
                            research_teams: prev.research_teams.map(
                              (team: any, idx: number) =>
                                idx === teamIndex
                                  ? { ...team, topics: updater(team.topics) }
                                  : team,
                            ),
                          }));
                        } else {
                          setFormState((prev: any) => ({
                            ...prev,
                            internal_rsearc_topics: updater(
                              prev.internal_rsearc_topics,
                            ),
                          }));
                        }
                      }}
                      edit={edit}
                      requestId={formState.id}
                      researchTeamId={formState.research_teams?.[teamIndex]?.id}
                      updateRequest={(token, id, data) =>
                        updateLegalResearchRequest(id, data, token)
                      }
                      finding_assesement={formState.finding_assesement}
                      forward_status={formState.forward_status}
                      schedules={[...(formState.schedules || []), ...(formState.internal_topic_schdule || [])]}
                    />
                    <div className="flex w-full">
                      {(((contextData?.user?.roles?.some(
                        (p) => p === "LegalResearchCoordinator",
                      ) ||
                        contextData?.user?.roles?.some(
                          (p) => p === "LRDIFUDDirector",
                        ) ||
                        contextData?.user?.roles?.some(
                          (p) => p === "OAGBManagementHead",
                        )) &&
                        (formState.status ===
                          LEGAL_RESEARCH_REQUEST_STATUS.title_presentation
                            .value ||
                          formState.status ===
                            LEGAL_RESEARCH_REQUEST_STATUS.title_requested
                              .value ||
                          (contextData?.user?.roles?.some(
                            (p) => p === "LegalResearchCoordinator",
                          ) &&
                            formState.status ===
                              LEGAL_RESEARCH_REQUEST_STATUS.title_accepted
                                .value)) &&
                        (formState.forward_status ===
                          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management
                            .value ||
                          formState.forward_status ===
                            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director
                              .value ||
                          formState.forward_status ===
                            LEGAL_RESEARCH_REQUEST_STATUS
                              .forwarded_to_coordinator.value)) ||
                        edit) && (
                        <button
                          className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md ml-auto mb-2"
                          onClick={handleAcceptTopics}
                        >
                          <div className="flex justify-center">
                            <p>{t("Submit")}</p>
                          </div>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          {formState.requested_by === "external" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6 mb-8 text-gray-700 text-sm">
              {formState.letter_reference_number && (
                <div>
                  <p className="font-medium text-gray-500">{t("Letter")}</p>
                  <p className="font-semibold text-gray-800">
                    # {formState.letter_reference_number}
                  </p>
                </div>
              )}
              <div>
                <p className="font-medium text-gray-500">{t("Date")}</p>
                <p className="font-semibold text-gray-800">
                  {formatDate(formState.created_at ?? "")}
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-500">{t("RequestBy")}</p>
                <p className="font-semibold text-gray-800">
                  {formState.requested_by}
                </p>
              </div>
              {formState.assigned_by && (
                <div>
                  <p className="font-medium text-gray-500">{t("AssignedBy")}</p>
                  <p className="font-semibold text-gray-800">
                    {formState?.assigned_by ?? "- -"}
                  </p>
                </div>
              )}
              {formState.assigned_coordinator_id && (
                <div>
                  <p className="font-medium text-gray-500">{t("AssignedCoordinator")}</p>
                  <p className="font-semibold text-gray-800">
                    {formState.assigned_coordinator_name}
                  </p>
                </div>
              )}
              {formState.urgency_level &&
                formState.requested_by === "external" && (
                  <div>
                    <p className="font-medium text-gray-500 mb-3">
                      {t("LevelOfUrgency")}
                    </p>
                    <span
                      className={getUrgencyButtonClasses(
                        formState?.urgency_level.charAt(0).toUpperCase() +
                          formState?.urgency_level.slice(1).toLowerCase(),
                      )}
                    >
                      {formState?.urgency_level}
                    </span>
                  </div>
                )}
              {formState.reason && (
                <div>
                  <p className="font-medium text-gray-500">{t("RequestType")}</p>
                  <p className="font-semibold text-gray-800">
                    {" "}
                    {
                      LEGAL_RESEARCH_REQUEST_TYPES.find(
                        (r) => r.value === formState.reason,
                      )?.label
                    }
                  </p>
                </div>
              )}
            </div>
          )}
          {formState?.implementation_direction && (
            <div className="flex w-full gap-8">
              <div className="flex flex-col flex-1 max-w-4xl bg-white rounded-lg shadow-md p-6 mb-4">
                <p className="font-semibold text-base mb-1">
                  {t("ImplementationDirection")}
                </p>
                <p className="text-[#B8B8B8] font-regular text-sm mb-1">
                  {formatDate(formState.updated_at)}
                </p>
                <p className="text-[#606060] font-regular text-base mb-1">
                  {formState?.implementation_direction}
                </p>
              </div>
            </div>
          )}
          {(formState?.proposal_summary ||
            formState?.proposal_file ||
            formState?.documents?.find((document: any) =>
              (document.document_type || "").includes("proposal"),
            )) && (
            <div className="border border-gray-400 p-4 mb-8 rounded-lg">
              {/* Top action bar — professional Re-upload Proposal button */}
              {canEditProposal && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setIsUploadProposalModalOpen(true)}
                    className="flex items-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {t("ReuploadProposal")}
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h2 className="font-semibold text-base text-gray-800">
                    {t("ProposalFileSummary")}
                  </h2>
                </div>
                {canEditProposal && (
                  <button
                    onClick={() => {
                      setEditedProposalSummary(
                        formState.proposal_summary || "",
                      );
                      setIsProposalSummaryEditing(!isProposalSummaryEditing);
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 text-sm font-semibold px-3 py-1 rounded-md transition-colors"
                  >
                    {isProposalSummaryEditing
                      ? t("CancelEdit")
                      : t("EditSummary")}
                  </button>
                )}
              </div>
              
              {isProposalSummaryEditing ? (
                <div className="mb-4">
                  <textarea
                    className="w-full p-3 border border-gray-300 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                    placeholder={t("EnterProposalSummaryPlaceholder")}
                    value={editedProposalSummary}
                    onChange={(e) => setEditedProposalSummary(e.target.value)}
                  />
                  <button
                    onClick={handleUpdateProposalSummary}
                    disabled={loading}
                    className="mt-2 bg-blue-600 hover:bg-blue-700 text-white text-base py-2 px-4 rounded-lg"
                  >
                    {loading ? t("Saving") : t("SaveSummary")}
                  </button>
                </div>
              ) : (
                <p className="mt-2 mb-4 text-[#606060] whitespace-pre-wrap leading-relaxed">
                  {formState.proposal_summary || (
                    <span className="text-gray-400 italic">
                      {t("NoProposalSummaryProvided")}
                    </span>
                  )}
                </p>
              )}
              
              {(() => {
                const proposalDocuments = formState?.documents
                  ?.filter((document: any) => (document.document_type || "").includes("proposal"))
                  ?.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                const latestProposalFile = proposalDocuments?.[0];
                
                return (
                  <div className="mb-4">
                    <h3 className="font-semibold text-sm mb-2 text-gray-700">
                      {t("CurrentProposalFile")}
                    </h3>
                    {latestProposalFile ? (
                      <DocumentViewerModal
                        documents={[latestProposalFile]}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                      />
                    ) : formState?.proposal_file ? (
                      <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <FileView
                          title={t("ProposalFile")}
                          file={formState.proposal_file}
                          fileName={formState.proposal_file.substring(
                            formState.proposal_file.lastIndexOf("/") + 1,
                          )}
                        />
                      </div>
                    ) : (
                      <p className="text-gray-400">{t("NoProposalFileAvailable")}</p>
                    )}
                  </div>
                );
              })()}
              
              {(() => {
                const proposalDocuments = formState?.documents
                  ?.filter((document: any) => (document.document_type || "").includes("proposal"))
                  ?.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                const previousProposalFiles = proposalDocuments?.slice(1) || [];
                const historyProposalFiles = (formState?.file_histories?.filter(
                  (fileHistroy: any) => fileHistroy.file_type === "proposal" && fileHistroy.file,
                ) || []);
                
                const hasPreviousFiles = previousProposalFiles.length > 0 || historyProposalFiles.length > 0;
                if (!hasPreviousFiles) {
                  return null;
                }
                
                return (
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() =>
                        setShowProposalHistory(!showProposalHistory)
                      }
                      className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm"
                    >
                      <span>{t("PreviousProposalFiles", { count: previousProposalFiles.length + historyProposalFiles.length })}</span>
                      <svg
                        className={`transform transition-transform ${showProposalHistory ? "rotate-180" : ""}`}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {showProposalHistory && (
                      <div className="p-4 space-y-4 bg-white">
                        {previousProposalFiles?.map(
                          (file: any, index: number) => (
                            <div key={`current-${index}`} className="border border-gray-200 p-3 rounded-lg">
                              <p className="text-xs text-gray-500 mb-2">
                                {t("VersionNumber", { version: previousProposalFiles.length - index, date: "" })}
                              </p>
                              <DocumentViewerModal
                                documents={[file]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                              />
                            </div>
                          ),
                        )}
                        {historyProposalFiles
                          ?.slice()
                          .reverse()
                          .map(
                            (fileHistroy: any, index: number) => (
                              <div key={`history-${index}`} className="border border-gray-200 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-2">
                                  {t("VersionNumber", { version: historyProposalFiles.length - index, date: "" })}
                                </p>
                                <FileView
                                  title={t("Proposal")}
                                  file={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/${fileHistroy?.file}`}
                                  fileName={
                                    fileHistroy?.file &&
                                    fileHistroy?.file.substring(
                                      fileHistroy?.file.lastIndexOf("/") + 1,
                                    )
                                  }
                                />
                              </div>
                            ),
                          )}
                      </div>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const isDirector = contextData?.user?.roles?.some(
                  (p) => p === "LRDIFUDDirector",
                );
                const isCoordinator = contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator",
                );
                const isGeneralAttorney = contextData?.user?.roles?.some(
                  (p) => p === "GeneralAttorney",
                );
                const proposalActionable =
                  formState?.status === "proposal_requested" ||
                  formState?.status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value ||
                  formState?.status === "proposal_approved" ||
                  isProposalSummaryEditing ||
                  hasProposalBeenEdited;
                const canReviewProposal =
                  (isDirector &&
                    formState?.forward_status !==
                      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
                    proposalActionable) ||
                  (isCoordinator &&
                    formState?.forward_status !==
                      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director
                        .value &&
                    proposalActionable);
                const canScheduleProposal =
                  formState?.status === "proposal_approved" &&
                  formState?.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
                  isGeneralAttorney &&
                  !formState.schedules?.find(
                    (schedule: any) =>
                      schedule.purpose === "proposal_presentation",
                  );

                if (!canReviewProposal && !canScheduleProposal) return null;

                return (
                  <div className="flex flex-wrap items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                    {canReviewProposal && (
                      <>
                        <button
                          onClick={handleProposalRespondBackClick}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium text-base py-2 px-8 rounded-lg transition-colors"
                        >
                          {t("RespondBack")}
                        </button>
                        <button
                          onClick={handleProposalApprove}
                          disabled={loading}
                          className="bg-green-600 hover:bg-green-700 text-white font-medium text-base py-2 px-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60"
                        >
                          {loading ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              {t("Approving")}
                            </>
                          ) : (
                            t("Approve")
                          )}
                        </button>
                      </>
                    )}
                    {canScheduleProposal && (
                      <button
                        onClick={() => setIsScheduleModalOpen(!isScheduleModalOpen)}
                        className="bg-[#212121] hover:bg-[#333333] text-white text-base px-8 py-2 rounded-lg transition-colors"
                      >
                        {t("ScheduleProposalPresentation")}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Topic Section */}
          <div className="flex flex-col mb-2 border border-gray-300 p-3 rounded-lg">
            {formState.finding_assesement && (
              <div>
                <div className="flex">
                  <p className="font-semibold text-base mb-2">
                    {t("AssessmentSummary")}
                  </p>
                </div>
                <p className="text-[#606060] font-normal text-base leading-relaxed">
                  {formState.assesement_summary}
                </p>
              </div>
            )}
            <br />
            {formState.request_summary && (
              <div>
                <div className="flex">
                  <p className="font-semibold text-base mb-2">
                    {t("RequestSummary")}
                  </p>
                </div>
                <p className="text-[#606060] font-normal text-base leading-relaxed">
                  {formState.request_summary}
                </p>
              </div>
            )}
            {formState?.documents.find(
              (document: any) =>
                document.document_type === "request_letter_file",
            ) && (
              <DocumentViewerModal
                documents={formState?.documents.filter(
                  (document: any) =>
                    document.document_type === "request_letter_file",
                )}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
              />
            )}
            {formState?.documents.find(
              (document: any) =>
                document.document_type === "committee_request_file",
            ) && (
              <DocumentViewerModal
                documents={formState?.documents.filter(
                  (document: any) =>
                    document.document_type === "committee_request_file",
                )}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
              />
            )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
              formState.title_remark && (
                <div>
                  <p className="font-semibold text-base mb-2">
                    {t("TopicApprovalRemark")}
                  </p>
                  <p className="text-[#606060] font-normal text-base leading-relaxed">
                    {formState.title_remark}
                  </p>
                </div>
              )}
            {formState.requested_by !== "internal" &&
              formState?.research_topic && (
                <div>
                  <div className="flex">
                    <p className="font-semibold text-base mb-2">
                      {t("Title")}: {formState.research_topic}
                    </p>
                    {contextData?.user?.roles?.some(
                      (p) => p === "LegalResearchProsecutor",
                    ) &&
                      formState?.prosecutor_assignments.find(
                        (prosecutor: any) =>
                          prosecutor.prosecutor_user_id ===
                          contextData?.user?.user_id,
                      ).is_team_leader &&
                      formState.forward_status ===
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value &&
                      (formState.status ===
                        LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                        formState.status ===
                          LEGAL_RESEARCH_REQUEST_STATUS.title_revision
                            .value) && (
                        <svg
                          onClick={() =>
                            navigate(
                              `/conduct-legal-research/editInternalrequest/${formState.id}`,
                              { state: { formState: formState } },
                            )
                          }
                          className="ml-auto mb-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                          width="25"
                          height="24"
                          viewBox="0 0 25 24"
                          fill="black"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                            fill="black"
                          />
                        </svg>
                      )}
                  </div>
                  <p className="text-[#606060] font-normal text-base leading-relaxed">
                    {formState.research_topic_abstract}
                  </p>
                </div>
              )}
            {/* Documents */}
            <div className="mt-2">
              {formState?.documents.find(
                (document: any) =>
                  document.document_type === "research_synopsis_file" ||
                  document.document_type === "synopsis",
              ) && (
                <DocumentViewerModal
                  documents={formState?.documents.filter(
                    (document: any) =>
                      document.document_type === "research_synopsis_file" ||
                      document.document_type === "synopsis",
                  )}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                />
              )}
              {formState?.finding_assesement && (
                <div className="flex items-center justify-between p-3">
                  <FileView
                    title={t("FindingAssessment")}
                    file={formState?.finding_assesement}
                    fileName={
                      formState?.finding_assesement &&
                      formState?.finding_assesement.substring(
                        formState?.finding_assesement.lastIndexOf("/") + 1,
                      )
                    }
                  />
                  {formState?.file_histories?.find(
                    (fileHistroy: any) => fileHistroy.file_type === "synopsis",
                  ) && (
                    <svg
                      onClick={() =>
                        setShowSynopsisHistory(!showSynopsisHistory)
                      }
                      className="my-auto ml-4 h-8"
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="8"
                      viewBox="0 0 10 8"
                      fill="none"
                    >
                      <path
                        d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z"
                        fill="#D9D9D9"
                      />
                    </svg>
                  )}
                </div>
              )}
              {showSynopsisHistory && (
                <div className="border border-gray-300 p-5 mx-5 rounded-lg space-y-4">
                  {formState?.file_histories
                    ?.slice()
                    .reverse()
                    .map(
                      (fileHistroy: any) =>
                        fileHistroy.file_type === "synopsis" && (
                          <div className="flex itmes-center justify-between ">
                            <FileView
                              title={t("ResearchSynopsis")}
                              file={fileHistroy?.file}
                              fileName={
                                fileHistroy?.file &&
                                fileHistroy?.file.substring(
                                  fileHistroy?.file.lastIndexOf("/") + 1,
                                )
                              }
                            />
                          </div>
                        ),
                    )}
                </div>
              )}

              {formState?.documents.find(
                (document: any) =>
                  document.document_type === "letter_file" ||
                  document.document_type === "letter",
              ) && (
                <DocumentViewerModal
                  documents={formState?.documents.filter(
                    (document: any) =>
                      document.document_type === "letter_file" ||
                      document.document_type === "letter",
                  )}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                />
              )}
            </div>
          </div>
          {formState.reports?.length > 0 && (
            <div className="my-6 bg-white border border-red-200 rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-600 text-white p-2 rounded-full mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black">
                  {t("OfficialReports")}
                </h3>
              </div>
              {formState.reports
                .sort(
                  (a: any, b: any) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime(),
                )
                .map((report: any, index: number) =>
                  index === 0 ? (
                    <div
                      key={report.id}
                      className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-r-lg"
                    >
                      <div className="flex items-center mb-3">
                        <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold mr-3">
                          {t("Latest")}
                        </div>
                        <h4 className="text-lg font-semibold text-black">
                          {t("CommitteeReport")}
                        </h4>
                      </div>
                      <p className="text-gray-700 font-normal text-base leading-relaxed mb-3">
                        {report.research_committe_report_summary}
                      </p>
                      <DocumentViewerModal
                        documents={report.documents.filter(
                          (document: any) =>
                            document.document_type === "committee_report_file",
                        )}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/research-committee-report-documents/document`}
                      />
                    </div>
                  ) : (
                    <details
                      key={report.id}
                      className="mb-3 border border-gray-200 rounded-lg"
                    >
                      <summary className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 font-medium text-gray-800 rounded-t-lg flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {t("ReportNumber", { number: formState.reports.length - index })}
                      </summary>
                      <div className="p-4 bg-white">
                        <p className="text-gray-700 font-normal text-base leading-relaxed mb-3">
                          {report.research_committe_report_summary}
                        </p>
                        <DocumentViewerModal
                          documents={report.documents.filter(
                            (document: any) =>
                              document.document_type ===
                              "committee_report_file",
                          )}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/research-committee-report-documents/document`}
                        />
                      </div>
                    </details>
                  ),
                )}
            </div>
          )}

          {formState.requested_by !== "internal" && (
            <>
              <div>
                <div className="flex">
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("Stakeholder")}
                  </p>
                  <p className="ml-auto text-[#0A1D39] font-semibold text-base">
                    {formState?.organization_name}
                  </p>
                </div>
                <hr className="w-full border-t-2 border-gray-300 my-4" />
                <div className="flex">
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("StakeholderType")}
                  </p>
                  <p className="ml-auto text-[#0A1D39] font-semibold text-base">
                    {formState?.organization_type}
                  </p>
                </div>
                <hr className="w-full border-t-2 border-gray-300 my-4" />
                <div className="flex">
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("AppliedDate")}
                  </p>
                  <p className="ml-auto text-[#0A1D39] font-semibold text-base">
                    {formatDate(formState?.created_at)}
                  </p>
                </div>
                <hr className="w-full border-t-2 border-gray-300 my-4" />
                <div className="flex">
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("Address")}
                  </p>
                  <p className="ml-auto text-[#0A1D39] font-semibold text-base">
                    {formState?.organization_state},{" "}
                    {formState?.organization_zone},{" "}
                    {formState?.organization_kebele}
                  </p>
                </div>
                <hr className="w-full border-t-2 border-gray-300 my-4" />
                <div className="flex">
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("Email")}
                  </p>
                  <p className="ml-auto text-[#0A1D39] font-semibold text-base">
                    {formState?.email_user_organization}
                  </p>
                </div>
                <hr className="w-full border-t-2 border-gray-300 my-4" />
              </div>
              <br />
            </>
          )}
          {formState?.remark_for_director && (
            <div className="mb-8">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkOfHeadOfOAGB")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.remark_for_director}
              </p>
            </div>
          )}
          {formState?.remark_for_coordinator && (
            <div className="mb-8">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkOfDirector")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.remark_for_coordinator}
              </p>
            </div>
          )}
          {formState?.remark_for_team && (
            <div className="mb-8">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkOfCoordinator")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.remark_for_team}
              </p>
            </div>
          )}

          {/* Team List - Show for internal requests with research_teams or external requests with prosecutor_assignments */}
          {formState?.requested_by === "internal" &&
            formState?.research_teams &&
            formState?.research_teams.length !== 0 &&
            formState?.research_teams
            .map((team: any, index: number) => (
                <div
                  key={index}
                  className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200"
                >
                  <ActionAvatars
                    title={
                      formState.requested_by === "internal"
                        ? team.team_name
                        : t("TeamList")
                    }
                    users={
                      formState.requested_by === "internal"
                        ? team.prosecutors.filter(
                            (prosecutor: any) =>
                              prosecutor.team_type === "prosecutor_topic_team",
                          )
                        : formState?.prosecutor_assignments
                    }
                    prosecutor={true}
                  />
                  {(contextData?.user?.roles?.some(
                    (role: string) =>
                      role === "LegalResearchCoordinator" ||
                      role === "LRDIFUDDirector",
                  )) && (
                      <svg
                        onClick={() => {
                          setEditTeamId(index);
                          setIsEditTeamModalOpen(true);
                        }}
                        className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                          fill="black"
                        />
                      </svg>
                    )}
                  <svg
                    onClick={() => {
                      setTeamId(index);
                      setViewTeam(!viewTeam);
                    }}
                    className="mr-2 ml-auto my-auto cursor-pointer hover:bg-gray-300 rounded-lg p-1"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <title>{t("ViewTeam")}</title>
                    <mask
                      id="mask0_9_31087"
                      maskUnits="userSpaceOnUse"
                      x="1"
                      y="5"
                      width="22"
                      height="14"
                    >
                      <path
                        d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="1.33333"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                        fill="black"
                        stroke="black"
                        strokeWidth="1.33333"
                        strokeLinejoin="round"
                      />
                    </mask>
                    <g mask="url(#mask0_9_31087)">
                      <path d="M0 0H24V24H0V0Z" fill="#212121" />
                    </g>
                  </svg>
                </div>
              ))}
          {(formState?.requested_by === "external" ||
            formState?.request_summary) &&
            formState?.prosecutor_assignments &&
            formState?.prosecutor_assignments.length !== 0 && (
              <div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                <ActionAvatars
                  title={t("TeamList")}
                  users={formState?.prosecutor_assignments}
                  prosecutor={true}
                />
                {(contextData?.user?.roles?.some(
                  (role: string) =>
                    role === "LegalResearchCoordinator" ||
                    role === "LRDIFUDDirector",
                )) && (
                    <svg
                      onClick={() => {
                        setEditTeamId(0);
                        setIsEditTeamModalOpen(true);
                      }}
                      className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                <svg
                  onClick={() => {
                    setTeamId(0);
                    setViewTeam(!viewTeam);
                  }}
                  className="mr-2 ml-auto my-auto cursor-pointer hover:bg-gray-300 rounded-lg p-1"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <title>{t("ViewTeam")}</title>
                  <mask
                    id="mask0_ext_31087"
                    maskUnits="userSpaceOnUse"
                    x="1"
                    y="5"
                    width="22"
                    height="14"
                  >
                    <path
                      d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.33333"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                      fill="black"
                      stroke="black"
                      strokeWidth="1.33333"
                      strokeLinejoin="round"
                    />
                  </mask>
                  <g mask="url(#mask0_ext_31087)">
                    <path d="M0 0H24V24H0V0Z" fill="#212121" />
                  </g>
                </svg>
              </div>
            )}
          
          <br />
        </div>
        {formState?.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
          !schedules &&
          (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
           contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
            <button
              onClick={() => {
                setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
              }}
              className="bg-[#212121] text-white text-sm py-3 px-6 rounded-lg ml-6"
            >
              {t("ScheduleTopicsPresentation", { type: formState.requested_by === "internal" ? t("Topics") : t("Title") })}
            </button>
          )}
        {((formState?.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
          !schedules) ||
        (formState?.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value &&
          !schedules)) &&
          (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
           contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
            <button
              onClick={() => {
                setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
              }}
              className="bg-[#212121] text-white text-sm py-3 px-6 rounded-lg ml-6"
            >
              {t("ScheduleTopicPresentation")}
            </button>
          )}
        {/* Upload Proposal Button */}
        {contextData?.user?.roles?.some(
          (p) => p === "LegalResearchProsecutor",
        ) &&
          formState?.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
          (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_requested.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.research_committee_underway
                .value) &&
          formState?.prosecutor_assignments.find(
            (prosecutor: any) =>
              prosecutor.prosecutor_user_id === contextData?.user?.user_id,
          ).is_team_leader && (
            <button
              className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md w-full"
              onClick={handleUploadProposalClick}
            >
              <div className="flex justify-center">
                {formState?.proposal_summary && <>{t("Re")}</>}
                <p>
                  {formState.request_summary
                    ? t("UploadReport")
                    : t("UploadProposal")}
                </p>
              </div>
            </button>
          )}
        {/* Assign Coordinator Button */}
        {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
          !formState.request_summary &&
          formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (
            <div className="text-center flex flex-row gap-4">
              <button
                onClick={() => setIsRespondBackToRequesterFormOpen(true)}
                className="bg-gray-300 flex-1 text-black text-base py-2 px-8 rounded-lg ml-6"
              >
                {t("RespondBack")}
              </button>
              <button
                onClick={handleAssignCoordinatorClick}
                className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md flex-1"
              >
                <div className="flex justify-center">
                  {formState.remark_for_coordinator && <p>{t("Re")}</p>}
                  <p>{t("AssignCoordinator")}</p>
                </div>
              </button>
            </div>
          )}

        {/* Director acting as head for a committee request: write a remark, then
            forward to a coordinator or create the prosecutor team directly */}
        {formState.request_summary &&
          formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
          contextData?.user?.roles?.some(
            (p) => p === "LRDIFUDDirector",
          ) && (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <label
                htmlFor="committee-director-remark"
                className="block text-sm font-medium text-gray-500"
              >
                {t("Remark")}
              </label>
              <textarea
                id="committee-director-remark"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
                placeholder={t("DetailHere")}
                value={remark}
                onChange={handleRemarkChange}
              ></textarea>
              <div className="mt-4 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCommitteeForwardToCoordinator}
                  disabled={loading}
                  className="bg-white border border-gray-800 text-gray-800 hover:bg-gray-100 rounded-lg px-6 py-2 font-semibold disabled:opacity-60"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting")}
                    </div>
                  ) : (
                    t("ForwardToCoordinator")
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleAssignProsecutorClick}
                  className="bg-red-900 hover:bg-red-800 text-white rounded-lg px-6 py-2 font-semibold"
                >
                  {t("AssignProsecutors")}
                </button>
              </div>
            </div>
          )}

        {formState.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
          !formState?.request_summary &&
          (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
            contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy") ||
            contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
          (!formState.remark_for_director || edit) && (
            <form onSubmit={handleRespondBack}>
              <>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <label className="block text-sm font-medium text-gray-500 min-w-[120px]">
                    {t("SetLevelOfUrgency")}
                  </label>
                  <br />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={getUrgencyButtonClasses("High")}
                      onClick={() => handleUrgencyClick("High")}
                    >
                      {t("High")}
                    </button>
                    <button
                      type="button"
                      className={getUrgencyButtonClasses("Medium")}
                      onClick={() => handleUrgencyClick("Medium")}
                    >
                      {t("Medium")}
                    </button>
                    <button
                      type="button"
                      className={getUrgencyButtonClasses("Low")}
                      onClick={() => handleUrgencyClick("Low")}
                    >
                      {t("Low")}
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <label
                    htmlFor="remark"
                    className="block text-sm font-medium text-gray-500"
                  >
                    {t("Remark")}
                  </label>
                  <textarea
                    id="remark"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
                    placeholder={t("DetailHere")}
                    required
                    value={remark}
                    onChange={handleRemarkChange}
                  ></textarea>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                  <button
                    type="submit"
                    className="ml-auto px-6 py-3 rounded-md bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        {LoadingIcon} {t("Submitting")}
                      </div>
                    ) : (
                      t("RespondBack")
                    )}
                  </button>
                  <div
                    onClick={handleSubmit}
                    className="cursor-pointer px-6 py-3 rounded-md bg-gray-800 text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        {LoadingIcon} {t("Submitting")}
                      </div>
                    ) : (
                      t("Submit")
                    )}
                  </div>
                </div>
              </>
            </form>
          )}
        {/* Assign Coordinator Button */}
        {contextData?.user?.roles?.some(
          (p) => p === "LegalResearchCoordinator",
        ) && (
          <div className="text-center">
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              (!formState.assigned_coordinator_id ||
                formState.assigned_coordinator_id ===
                  contextData.user.user_id ||
                formState.request_summary) &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (
                <button
                  onClick={handleAssignProsecutorClick}
                  className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md w-full"
                >
                  <div className="flex justify-center">
                    {formState.remark_for_team && <p>{t("Re")}</p>}
                    <p>{t("AssignProsecutors")}</p>
                  </div>
                </button>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.research_committee_acceptd.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                  .value && (
                <button
                  onClick={handleAssignProsecutorClick}
                  className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md w-full mt-4"
                >
                  <div className="flex justify-center">
                    <p>{t("AssignProsecutors")}</p>
                  </div>
                </button>
              )}
          </div>
        )}
        {/* Head forwards the reviewed request on to the director */}
        {formState.remark_for_director &&
          formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
          (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
            contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
            <button
              onClick={() =>
                handleForward(
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value,
                )
              }
              type="button"
              className="bg-[#212121] text-white rounded-lg p-2 px-8 mt-2"
            >
              {t("ForwardToDirector")}
            </button>
          )}

        {/* Director acting as head: skip the director step and forward straight
            to a coordinator, or optionally set up the prosecutor team directly */}
        {formState.remark_for_director &&
          formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
          contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") && (
            <div className="flex flex-wrap gap-4 mt-2">
              <button
                onClick={() => setIsForwardCoordinatorModalOpen(true)}
                type="button"
                className="bg-[#212121] text-white rounded-lg p-2 px-8"
              >
                {t("ForwardToCoordinator")}
              </button>
              <button
                onClick={handleAssignProsecutorClick}
                type="button"
                className="bg-white border border-gray-800 text-gray-800 rounded-lg p-2 px-8"
              >
                {t("SetupTeamAndForwardToProsecutor")}
              </button>
            </div>
          )}
      </div>
      {viewTeam && (
        <TeamView
          teamMembers={
            formState.requested_by === "internal"
              ? formState.research_teams?.[teamId].prosecutors
              : formState.prosecutor_assignments
          }
          onClose={() => setViewTeam(false)}
        />
      )}
      {((contextData?.user?.roles?.some(
        (p) => p === "LegalResearchCoordinator",
      ) &&
        (formState?.proposal_deadline !== "" || formState?.request_summary)) ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
          <SettingUpTeamModal
            request={formState}
            setLegalResearch={setFormState}
            isOpen={isTeamModalOpen}
            onClose={handleCloseTeamModal}
            onAssignProsecutor={handleAssignProsecutorClick}
          />
        )}
      {(contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
        <EditTeamModal
          request={formState}
          teamId={editTeamId}
          isOpen={isEditTeamModalOpen}
          onClose={() => setIsEditTeamModalOpen(false)}
        />
      )}
      {(contextData?.user?.roles?.some(
        (p) => p === "LegalResearchCoordinator",
      ) ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
        <RespondBackForm
          request={formState}
          setLegalResearch={setFormState}
          isOpen={isRespondBackFormOpen}
          onClose={() => setIsRespondBackFormOpen(false)}
          forward={respondBackForward}
          status={respondBackStatus}
        />
      )}
      {(contextData?.user?.roles?.some(
        (p) => p === "LegalResearchCoordinator",
      ) ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
        <RespondBackToRequesterForm
          request={formState}
          setLegalResearch={setFormState}
          isOpen={isRespondBackToRequesterFormOpen}
          onClose={() => setIsRespondBackToRequesterFormOpen(false)}
          forward={respondBackForward}
          status={respondBackStatus}
        />
      )}
      {contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
        formState?.prosecutor_assignments?.find(
          (prosecutor: any) =>
            prosecutor.prosecutor_user_id === contextData?.user?.user_id,
        )?.is_team_leader && (
          <UploadProposal
            request={formState}
            setLegalResearch={setFormState}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onUploadProposal={handleReuploadProposal}
          />
        )}

      {(contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
        <UploadProposal
          request={{
            ...formState,
            status: LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value,
          }}
          setLegalResearch={setFormState}
          isOpen={isUploadProposalModalOpen}
          onClose={() => setIsUploadProposalModalOpen(false)}
          onUploadProposal={handleReuploadProposal}
        />
      )}

      {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") && (
        <AssignCoordinatorModal
          data={formState}
          setLegalResearch={setFormState}
          isOpen={isAssignModalOpen}
          onClose={handleAssignCloseModal}
        />
      )}
      {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") && (
        <ForwardToCoordinatorModal
          data={formState}
          setLegalResearch={setFormState}
          isOpen={isForwardCoordinatorModalOpen}
          onClose={() => setIsForwardCoordinatorModalOpen(false)}
        />
      )}
      {(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) && (
        <ScheduleTitlePresentationModal
          data={formState}
          setLegalResearch={setFormState}
          isOpen={isScheduleTitleModalOpen}
          onClose={handleSchduleTitleCloseModal}
        />
      )}
      {(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy")) &&
        formState.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value && (
          <SchedulePresentationModal
            data={formState}
            setLegalResearch={setFormState}
            isOpen={isScheduleModalOpen}
            onClose={handleSchduleCloseModal}
          />
        )}
    </>
  );
}

export default ProposalDocument;